import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircleIcon, PencilIcon } from "@heroicons/react/20/solid";
import Loading from "./loading";

const STATUS_STEPS = [
  { key: 1, label: "New Complaint", color: "red" },
  { key: 2, label: "Meeting with Accuser Complete", color: "orange" },
  { key: 3, label: "Witness Statements Requested", color: "yellow" },
  { key: 4, label: "Witness Statements Received", color: "purple" },
  { key: 5, label: "Meeting with Perpetrator", color: "gray" },
  { key: 6, label: "Deliberation", color: "indigo" },
  { key: 7, label: "Complete", color: "emerald" },
];

const EDITABLE_FIELDS = [
  { label: "Employee Perspective", key: "EmployeePerspective" },
  { label: "Agree with Incident", key: "AgreeWithIncident" },
  { label: "Explanation", key: "Explanation" },
  { label: "Steps Taken", key: "StepsTaken" },
  { label: "Findings", key: "Findings" },
  { label: "Disciplinary Action", key: "DisciplinaryAction" },
  { label: "Rationale", key: "Rationale" },
  { label: "Corrective Actions", key: "CorrectiveActions" },
  { label: "Support Provided", key: "SupportProvided" },
  { label: "Improvement Deadline", key: "ImprovementDeadline" },
];

function StatusBadge({ status }) {
  const step = STATUS_STEPS.find((s) => s.key === status) || STATUS_STEPS[0];
  return (
    <span
      className={`inline-block px-4 py-1 rounded-full font-semibold text-sm bg-${step.color}-100 text-${step.color}-700`}
    >
      {step.label}
    </span>
  );
}

function StatusStepper({ status, onBack, onForward }) {
  return (
    <div className="flex items-center w-full max-w-xl mx-auto">
      <button
        className="mr-2 flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white text-emerald-500 hover:bg-emerald-50 disabled:opacity-50"
        onClick={onBack}
        disabled={status === 1}
        aria-label="Previous Status"
        data-component="disciplinary-status-stepper"
        data-feature="status_back_put"
      >
        <span className="text-2xl">&#8592;</span>
      </button>
      <div className="flex items-center space-x-0 overflow-x-auto">
        {STATUS_STEPS.map((step, idx) => {
          const isActive = step.key === status;
          const isCompleted = step.key < status;
          return (
            <div key={step.key} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full border-2 font-bold text-xs
                    ${isActive
                      ? "bg-indigo-600 border-indigo-600 text-white shadow-lg"
                      : isCompleted
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : "bg-white border-gray-300 text-gray-400"}
                  `}
                >
                  {step.key}
                </div>
                <span
                  className={`mt-1 text-[10px] font-medium text-center w-16 truncate
                    ${isActive
                      ? "text-indigo-700"
                      : isCompleted
                      ? "text-emerald-700"
                      : "text-gray-500"}
                  `}
                  style={{ maxWidth: 64 }}
                >
                  {step.label}
                </span>
              </div>
              {idx !== STATUS_STEPS.length - 1 && (
                <div
                  className={`w-5 h-1 mx-1 rounded-full ${
                    step.key < status ? "bg-emerald-500" : "bg-gray-200"
                  }`}
                ></div>
              )}
            </div>
          );
        })}
      </div>
      <button
        className="ml-2 flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white text-red-500 hover:bg-red-50 disabled:opacity-50"
        onClick={onForward}
        disabled={status === STATUS_STEPS.length}
        aria-label="Next Status"
        data-component="disciplinary-status-stepper"
        data-feature="status_forward_put"
      >
        <span className="text-2xl">&#8594;</span>
      </button>
    </div>
  );
}

function WitnessStatus({ appraisal, status, onAutoAdvance }) {
  const witnesses = appraisal?.Witnesses
    ? appraisal.Witnesses.split(",").map((w) => w.trim()).filter(Boolean)
    : [];
  const statementStaff = Array.isArray(appraisal?.statements)
    ? appraisal.statements.map((s) => s.StaffName?.trim()).filter(Boolean)
    : [];
  if (!witnesses.length) return null;
  const outstanding = witnesses.filter((w) => !statementStaff.includes(w));
  if (outstanding.length === 0) {
    if (status === 3 && appraisal?.id) onAutoAdvance();
    return (
      <div className="mb-2 text-emerald-600 font-semibold flex items-center">
        All witness statements received
        <CheckCircleIcon className="h-5 w-5 ml-2" />
      </div>
    );
  }
  return (
    <div className="mb-2 text-yellow-700 font-semibold">
      Outstanding witness statements from: {outstanding.join(", ")}
    </div>
  );
}

// EditableField now supports importAISummary prop for EmployeePerspective
function EditableField({
  label,
  value,
  fieldKey,
  appraisalId,
  onUpdated,
  type = "text",
  importAISummary,
}) {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleSave = async () => {
    setLoading(true);
    let sendValue = inputValue;
    // For boolean fields
    if (fieldKey === "AgreeWithIncident") {
      sendValue = inputValue === "Yes" ? "Yes" : "No";
    }
    // For date fields
    if (fieldKey === "ImprovementDeadline" && inputValue === "") {
      sendValue = null;
    }
    await fetch("https://api.insightos.co.uk/api/updatefield", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        id: appraisalId,
        field: fieldKey,
        value: sendValue,
      }),
    });
    setEditing(false);
    setLoading(false);
    onUpdated && onUpdated();
  };

  // Render for boolean
  if (fieldKey === "AgreeWithIncident") {
    return (
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{label}:</span>
        {editing ? (
          <>
            <select
              className="border rounded px-2 py-1"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <button
              className="ml-2 px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              onClick={handleSave}
              disabled={loading}
              data-component="disciplinary-editable-field"
              data-feature="save_field_post"
            >
              Save
            </button>
            <button
              className="ml-1 px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={() => setEditing(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="text-gray-700">{value === true || value === "Yes" ? "Yes" : "No"}</span>
            <button
              className="ml-2 text-indigo-600 hover:text-indigo-900"
              onClick={() => setEditing(true)}
              aria-label={`Edit ${label}`}
            >
              <PencilIcon className="h-4 w-4 inline" />
            </button>
          </>
        )}
      </div>
    );
  }

  // Render for date
  if (fieldKey === "ImprovementDeadline") {
    return (
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{label}:</span>
        {editing ? (
          <>
            <input
              type="date"
              className="border rounded px-2 py-1"
              value={inputValue || ""}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <button
              className="ml-2 px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              onClick={handleSave}
              disabled={loading}
              data-component="disciplinary-editable-field"
              data-feature="save_field_post"
            >
              Save
            </button>
            <button
              className="ml-1 px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={() => setEditing(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="text-gray-700">
              {value
                ? format(new Date(value), "MMM dd, yyyy")
                : "N/A"}
            </span>
            <button
              className="ml-2 text-indigo-600 hover:text-indigo-900"
              onClick={() => setEditing(true)}
              aria-label={`Edit ${label}`}
            >
              <PencilIcon className="h-4 w-4 inline" />
            </button>
          </>
        )}
      </div>
    );
  }

  // Render for text/textarea
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium text-gray-900 flex items-center">
        {label}:
        {importAISummary && (
          <button
            type="button"
            className="ml-2 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
            onClick={importAISummary}
            disabled={editing}
            data-component="disciplinary-ai-actions"
            data-time-saved="5"
            data-feature="import_ai_summary_post"
          >
            Import AI Summary
          </button>
        )}
      </span>
      {editing ? (
        <>
          <textarea
            className="border rounded px-2 py-1 w-full min-h-[60px]"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            rows={3}
          />
          <div className="flex gap-2 mt-1">
            <button
              className="px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              onClick={handleSave}
              disabled={loading}
              data-component="disciplinary-editable-field"
              data-time-saved="20"
              data-feature="save_field_post"
            >
              Save
            </button>
            <button
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={() => setEditing(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="flex items-center">
          <span className="text-gray-700">{value || "N/A"}</span>
          <button
            className="ml-2 text-indigo-600 hover:text-indigo-900"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${label}`}
          >
            <PencilIcon className="h-4 w-4 inline" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ViewAppraisal() {
  const [appraisals, setAppraisals] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editIndex, setEditIndex] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [dateMeeting, setDateMeeting] = useState("");
  const [meeting, setMeeting] = useState(null);
  const [meetingTime, setMeetingTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [bookFinalMeeting, setBookFinalMeeting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetch("https://api.insightos.co.uk/api/witnessstatement", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setShowButton((data.data || []).length > 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!dateMeeting) {
      setMeeting(null);
      return;
    }
    fetch(`https://api.insightos.co.uk/api/meetings/${dateMeeting}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setMeeting(data.success ? data.data : []))
      .catch(() => setMeeting([]));
  }, [dateMeeting]);

  const getAppraisals = async () => {
    try {
      const res = await fetch("https://api.insightos.co.uk/api/disciplinarymanager", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await res.json();
      const nextAppraisals = data.data || [];
      setAppraisals(nextAppraisals);
      return nextAppraisals;
    } catch {
      setAppraisals([]);
      return [];
    }
  };

  useEffect(() => {
    getAppraisals();
  }, [refresh]);

  useEffect(() => {
    if (!location.state?.heavyAiRefreshKey) {
      return;
    }

    const disciplinaryId =
      location.state?.disciplinaryId || location.state?.heavyAiJobResult?.disciplinaryId;

    getAppraisals().then((nextAppraisals) => {
      if (!disciplinaryId) {
        return;
      }

      const nextIndex = nextAppraisals.findIndex(
        (appraisal) => String(appraisal.id) === String(disciplinaryId)
      );

      if (nextIndex >= 0) {
        setActiveIndex(nextIndex);
      }
    });
  }, [location.state?.heavyAiRefreshKey]);

  useEffect(() => {
    if (editIndex) navigate("/viewdisciplinary", { state: { id: editIndex } });
  }, [editIndex, navigate]);

  if (!appraisals.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-lg text-gray-500 mb-4">No Disciplinary's Present</p>
        <button
          onClick={() => navigate("/dashboard")}
          className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500"
        >
          Go Back
        </button>
      </div>
    );
  }

  const currentAppraisal = appraisals[activeIndex];
  const status = currentAppraisal?.Status || 1;

  const handleStatusChange = async (direction) => {
    let newStatus = status;
    if (direction === "back" && status > 1) newStatus = status - 1;
    else if (direction === "forward" && status < STATUS_STEPS.length) newStatus = status + 1;
    else return;
    await fetch(`https://api.insightos.co.uk/api/changestatus/${currentAppraisal.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ Status: newStatus }),
    });
    getAppraisals();
  };

  const handleRequestWitness = async (docType) => {
    await fetch("https://api.insightos.co.uk/api/requestwitness", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ DocType: docType }),
    });
    getAppraisals();
  };

  const autoAdvanceStatus = () => {
    fetch(`https://api.insightos.co.uk/api/changestatus/${currentAppraisal.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ Status: 4 }),
    }).then(getAppraisals);
  };

  const handleBookMeeting = async () => {
    setLoading(true);
    if (!meetingTime) {
      setLoading(false);
      return;
    }
    const response = await fetch("https://api.insightos.co.uk/api/bookpermeeting", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ date: dateMeeting, time: meetingTime, id: currentAppraisal.id }),
    });
    setLoading(false);
    setMeetingTime("");
    setDateMeeting("");
    getAppraisals();
    const data = await response.json();
    if (data.success) {
      alert(data.message || "Meeting booked. Please check your emails.");
    } else {
      alert(data.message || "Failed to book meeting");
    }
  };

  const handleFinalBookMeeting = async () => {
    setLoading(true);
    if (!meetingTime) {
      setLoading(false);
      return;
    }
    const response = await fetch("https://api.insightos.co.uk/api/bookfinalmeeting", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ date: dateMeeting, time: meetingTime, id: currentAppraisal.id }),
    });
    setLoading(false);
    setMeetingTime("");
    setDateMeeting("");
    getAppraisals();
    const data = await response.json();
    if (data.success) {
      alert(data.message || "Meeting booked. Please check your emails.");
    } else {
      alert(data.message || "Failed to book meeting");
    }
  };

  // Helper for editable fields
  const getFieldValue = (key) => {
    if (key === "AgreeWithIncident") {
      if (currentAppraisal?.AgreeWithIncident === "Yes") return "Yes";
      if (currentAppraisal?.AgreeWithIncident === "No") return "No";
      return "No";
    }
    if (key === "ImprovementDeadline") {
      return currentAppraisal?.ImprovementDeadline || "";
    }
    return currentAppraisal?.[key] || "";
  };

  // Handler for importing AI Summary for EmployeePerspective
  const handleImportAISummary = () => {
    if (!Array.isArray(currentAppraisal?.meeting)) return;
    // Find the meeting with Status === "Meeting with Perpetrator"
    const perpetratorMeeting = currentAppraisal.meeting.find(
      (m) => m.Status === "Meeting With Perpetrator" && m.AISummary
    );
    if (perpetratorMeeting && perpetratorMeeting.AISummary) {
      // Directly update the EmployeePerspective field in the backend
      fetch("https://api.insightos.co.uk/api/updatefield", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          id: currentAppraisal.id,
          field: "EmployeePerspective",
          value: perpetratorMeeting.AISummary,
        }),
      }).then(() => setRefresh((r) => r + 1));
    } else {
      alert("No AI Summary found for 'Meeting with Perpetrator'.");
    }
  };

  const handleAISummaryReport = async () => {

    setLoading(true);

    try {
      const response = await fetch("https://api.insightos.co.uk/api/generateaisummaryreport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: currentAppraisal.id }),
      });

      const result = await response.json();

      if (response.status === 202 && result.queued && result.jobId) {
        navigate(`/job-progress/${result.jobId}`, {
          state: {
            title: 'Generating AI summary report',
            successPath: '/dashboard',
            backPath: '/dashboard',
            successState: {
              dashboardTab: 'Management',
              managementTab: 'Discipline',
              disciplinaryId: currentAppraisal.id,
            },
          },
        });
        return;
      }

      if (response.ok) {
       getAppraisals()
       setLoading(false)
      } else {
        alert("Failed to generate AI summary report.");
      }
    }
    catch (error) {
      console.error("Error generating AI summary report:", error);
      alert("An error occurred while generating the report.");
    } finally {
      setLoading(false);
    }}



  return (
    <>
      {loading && <Loading />}
      {showButton && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => navigate("/witnessstatement")}
            className="bg-red-500 animate-bounce text-white px-4 py-2 rounded-lg shadow-lg hover:bg-red-600"
          >
            <span className="inline-block mr-1">🔔</span>
          </button>
        </div>
      )}
      <div className={`min-h-screen py-8 px-4 sm:px-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-indigo-500">
              <h3 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Disciplinary Management</h3>
              <p className="mt-2 text-sm text-gray-600">
                Comprehensive tracking and resolution of staff disciplinary incidents
              </p>
            </div>
            <div className="flex space-x-3 overflow-x-auto bg-white rounded-xl shadow-lg p-4">
              {appraisals.map((appraisal, idx) => (
                <button
                  key={idx}
                  className={`px-4 py-3 rounded-lg text-sm font-semibold border-2 transition-all duration-200 transform hover:scale-105 ${
                    idx === activeIndex
                      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-600 shadow-lg"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-indigo-50 hover:border-indigo-300"
                  }`}
                  onClick={() => setActiveIndex(idx)}
                >
                  {appraisal.StaffName || `Case ${idx + 1}`}
                </button>
              ))}
            </div>
            <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-emerald-500">
              <StatusBadge status={status} />
            </div>
          </div>

        <div className="mb-8 bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">Case Progress</h4>
          <StatusStepper
            status={status}
            onBack={() => handleStatusChange("back")}
            onForward={() => handleStatusChange("forward")}
          />
        </div>

        {Array.isArray(currentAppraisal?.meeting) && currentAppraisal.meeting.length > 0 && (
          <div className="mb-8 bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h4 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">📋 Meeting History</span>
            </h4>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-1">
              {currentAppraisal.meeting.map((meeting, idx) => {
                let meetingBg = "bg-gradient-to-r from-green-50 to-emerald-50";
                let meetingStatus = "Not Started";
                let statusColor = "text-gray-500";
                let statusIcon = "⏳";
                if (meeting.Finished === 1) {
                  meetingBg = "bg-gradient-to-r from-red-50 to-rose-50";
                  meetingStatus = "Completed";
                  statusColor = "text-red-600";
                  statusIcon = "✅";
                } else if (meeting.StartMeeting === 1) {
                  meetingBg = "bg-gradient-to-r from-blue-50 to-indigo-50";
                  meetingStatus = "In Progress";
                  statusColor = "text-blue-600";
                  statusIcon = "🔄";
                } else if (meeting.StartMeeting === 0 && meeting.Finished === 0) {
                  meetingBg = "bg-gradient-to-r from-yellow-50 to-amber-50";
                  meetingStatus = "Scheduled";
                  statusColor = "text-yellow-600";
                  statusIcon = "📅";
                }
                const showRequestWitness = status === 2 && meeting.Finished === 1;
                return (
                  <div
                    key={meeting.id || idx}
                    className={`rounded-xl shadow-lg ring-2 ring-gray-100 p-6 ${meetingBg} border-l-4 ${
                      meeting.Finished === 1 ? 'border-l-red-400' : 
                      meeting.StartMeeting === 1 ? 'border-l-blue-400' : 'border-l-yellow-400'
                    } transform hover:scale-[1.02] transition-all duration-200`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{statusIcon}</span>
                        <div>
                          <span className="font-bold text-gray-900 text-lg">
                            {meeting.Name || `Meeting ${idx + 1}`}
                          </span>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset bg-white ${statusColor} shadow-sm`}
                            >
                              {meetingStatus}
                            </span>
                            {meeting.Status && (
                              <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ring-gray-200 bg-white text-gray-700 shadow-sm">
                                {meeting.Status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {showRequestWitness && (
                        <button
                          className="inline-flex items-center rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
                          onClick={() => handleRequestWitness(meeting.DocType)}
                          data-component="disciplinary-meeting-actions"
                          data-time-saved="5"
                          data-feature="request_witness_post"
                        >
                          📝 Request Witness Statement
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="bg-white/60 rounded-lg p-3">
                        <dt className="font-semibold text-gray-900 flex items-center">
                          📅 Date
                        </dt>
                        <dd className="mt-1 text-gray-700">
                          {meeting.Date
                            ? format(new Date(meeting.Date), "MMM dd, yyyy")
                            : "N/A"}
                        </dd>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3">
                        <dt className="font-semibold text-gray-900 flex items-center">
                          🕐 Time
                        </dt>
                        <dd className="mt-1 text-gray-700">{meeting.Time || "N/A"}</dd>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3">
                        <dt className="font-semibold text-gray-900 flex items-center">
                          📍 Location
                        </dt>
                        <dd className="mt-1 text-gray-700">{meeting.Location || "N/A"}</dd>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3">
                        <dt className="font-semibold text-gray-900 flex items-center">
                          👥 Attendees
                        </dt>
                        <dd className="mt-1 text-gray-700">{meeting.Attendees || "N/A"}</dd>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 sm:col-span-2">
                        <dt className="font-semibold text-gray-900 flex items-center">
                          📝 Notes
                        </dt>
                        <dd className="mt-1 text-gray-700">{meeting.Notes || meeting.MeetingNotes || "N/A"}</dd>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 sm:col-span-2">
                        <dt className="font-semibold text-gray-900 flex items-center">
                          🤖 AI Summary
                        </dt>
                        <dd className="mt-1 text-gray-700 italic">{meeting.AISummary || "N/A"}</dd>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          {status === 1 && (
            <div className="mb-6 p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-l-4 border-red-400">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Initial Complaint Review</h4>
              <p className="text-red-700 mb-4">A new disciplinary complaint has been filed. Review the details below and schedule an initial meeting with the complainant.</p>
              <button
                onClick={() => {
                  // Set up for creating initial meeting
                  setDateMeeting("");
                  setMeetingTime("");
                  setBookFinalMeeting(false);
                  // Scroll to meeting booking section or show modal
                  document.getElementById('meeting-booking-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-x-2 rounded-lg bg-gradient-to-r from-red-600 to-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:from-red-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200"
              >
                📅 Create Initial Meeting
              </button>
            </div>
          )}
          {(status === 1 || status === 4) && (
            <div id="meeting-booking-section" className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-l-4 border-blue-400 shadow-lg">
              <div className="flex items-center mb-4">
                <span className="text-2xl mr-3">�</span>
                <div>
                  <h4 className="text-xl font-bold text-blue-800">
                    {status === 1 ? "Schedule Initial Meeting" : "Schedule Follow-up Meeting"}
                  </h4>
                  <p className="text-blue-700">
                    {status === 1 
                      ? "Set up an initial meeting to discuss the complaint with the complainant." 
                      : "Schedule meetings with the employee and other involved parties."
                    }
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Date:</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    onChange={(e) => {
                      setDateMeeting(e.target.value);
                      setMeetingTime("");
                    }}
                    value={dateMeeting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Time:</label>
                  <input
                    type="time"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                  />
                </div>
              </div>
              {meeting && meeting.length > 0 && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200 shadow-sm">
                  <h5 className="text-lg font-semibold mb-3 text-blue-900 flex items-center">
                    <span className="mr-2">📋</span>
                    Existing Meetings on {dateMeeting}:
                  </h5>
                  <ul className="space-y-2">
                    {meeting.map((meet, idx) => (
                      <li key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-blue-800 font-medium">{meet.Name} - {meet.Time}</span>
                        <span className="text-sm text-blue-600">{meet.FinishTime}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {meeting && meeting.length === 0 && dateMeeting && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200 shadow-sm">
                  <h5 className="text-lg font-semibold mb-2 text-green-900 flex items-center">
                    <span className="mr-2">✅</span>
                    Available Time Slot
                  </h5>
                  <p className="text-green-700">No conflicts detected. You can schedule a meeting at {meetingTime} on {dateMeeting}.</p>
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={status === 1 ? handleBookMeeting : handleBookMeeting}
                  disabled={!meetingTime || !dateMeeting}
                  className="inline-flex items-center gap-x-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-component="disciplinary-meeting-actions"
                  data-time-saved="10"
                  data-feature="schedule_meeting_post"
                >
                  <span>📅</span>
                  {status === 1 ? "Schedule Initial Meeting" : "Schedule Meeting"}
                </button>
              </div>
            </div>
          )}

                  {/* Only show the button and report in status 6, only report in status 7 */}
                  {(status === 6 || status === 7) && (
                    <div className="mb-6 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-l-4 border-yellow-400 shadow-lg">
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">🤖</span>
                      <div>
                        <h4 className="text-xl font-bold text-yellow-800">AI-Powered Analysis</h4>
                        <p className="text-yellow-700">
                        This AI-generated summary is a neutral consolidation of submitted information and does not reflect any disciplinary conclusions or findings. Final decisions are made solely by HR based on a full review of the case. Please use AI response as a reference only.
                        </p>
                      </div>
                    </div>
                      {currentAppraisal?.AIReport === null && (
                      <button
                        type="button"
                        onClick={handleAISummaryReport}
                        className="inline-flex items-center gap-x-2 rounded-lg bg-gradient-to-r from-yellow-600 to-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:from-yellow-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200"
                        data-component="disciplinary-ai-actions"
                        data-time-saved="60"
                        data-feature="generate_ai_summary_post"
                      >
                        <span>🤖</span>
                        Generate AI Summary
                      </button>
                      )}
                    </div>
                    )}
                    {currentAppraisal?.AIReport && (
                      <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-l-4 border-blue-400 shadow-lg">
                      <div className="flex items-center mb-4">
                        <span className="text-2xl mr-3">📊</span>
                        <h4 className="text-xl font-bold text-blue-800">AI Summary Report</h4>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        {currentAppraisal.AIReport
                        .split(/\n+/)
                        .map((line, idx) => {
                          if (line.trim().startsWith("###")) {
                          return (
                            <h5 key={idx} className="mt-4 mb-2 text-lg font-bold text-indigo-800 text-base border-b border-indigo-200 pb-1">
                            {line.replace(/^###\s*/, "")}
                            </h5>
                          );
                          }
                          if (line.trim().startsWith("**")) {
                          // Bold section, break before and after
                          return (
                            <p key={idx} className="font-semibold text-sm text-indigo-900 mt-3 mb-1 bg-indigo-50 p-2 rounded">
                            {line.replace(/^\*\*\s*/, "").replace(/\*\*$/, "")}
                            </p>
                          );
                          }
                            return <p key={idx} className="mb-2 text-sm leading-relaxed">{line}</p>;
                          })}
                          </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4">
                        {status === 6 && (
                          <div className="sm:col-span-3">
                          <p className="text-sm text-gray-700">
                            Please complete the entire form. When you are finished, click the button below to submit your report and schedule a final meeting with the employee to discuss the outcome.
                          </p>
                          <button onClick= {() => setBookFinalMeeting(true)}
                            type="button"
                            className="inline-flex items-center mt-4 gap-x-2 rounded-md bg-emerald-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                          >
                            <CheckCircleIcon aria-hidden="true" className="-ml-0.5 size-5" />
                            Submit Report & Book Final Meeting
                          </button>
                          {bookFinalMeeting && (
                            <div className="mt-4">
                              <label htmlFor="meetingDate" className="block text-sm font-medium text-gray-700">
                                Select Meeting Date:
                              </label>
                              <input
                                type="date"
                                id="meetingDate"
                                value={dateMeeting}
                                onChange={(e) => setDateMeeting(e.target.value)}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                              />
                              <>
                               {meeting && meeting.length === 0 && (
                    <div className="bg-purple-50 p-4 rounded-lg mt-4 border border-red-100">
                    <h3 className="text-lg font-semibold mb-2 text-red-900">
                    No meetings scheduled for this date.
                    </h3>
                    <label
                    htmlFor="bookMeeting"
                    className="block text-blue-900 font-medium mb-2"
                    >
                    Book Meeting Time
                    </label>
                    <input
                    id="bookMeeting"
                    name="bookMeeting"
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    className="block w-full rounded-lg bg-white px-3 py-2 border border-gray-300 text-base text-blue-900 focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                    onClick={handleFinalBookMeeting}
                    type="button"
                    className="inline-flex items-center mt-4 gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    data-component="disciplinary-meeting-actions"
                    data-time-saved="5"
                    data-feature="schedule_final_meeting_post"
                    >
                    <CheckCircleIcon aria-hidden="true" className="-ml-0.5 size-5" />
                    Save Meeting
                    </button>
                    </div>
                    )}
                    </>

                            {meeting && meeting.length > 0 && (
                    <div className="bg-blue-50 p-4 rounded-lg mt-4 border border-blue-100">
                    <h3 className="text-lg font-semibold mb-2 text-blue-900">
                    Meetings on {dateMeeting}:
                    </h3>
                    <ul className="list-disc pl-5 mb-3">
                    {meeting.map((meet, idx) => (
                      <li key={idx} className="text-blue-800">
                      {meet.Name} - {meet.Time} - {meet.FinishTime}
                      </li>
                    ))}
                    
                    </ul>
                    <label
                    htmlFor="bookMeeting"
                    className="block text-blue-900 font-medium mb-2"
                    >
                    Book Meeting Time
                    </label>
                    <input
                    id="bookMeeting"
                    name="bookMeeting"
                    type="time"
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    className="block w-full rounded-lg bg-white px-3 py-2 border border-gray-300 text-base text-blue-900 focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                    onClick={handleFinalBookMeeting}
                    type="button"
                    className="inline-flex items-center mt-4 gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    data-component="disciplinary-meeting-actions"
                    data-time-saved="5"
                    data-feature="schedule_final_meeting_post"
                    >
                    <CheckCircleIcon aria-hidden="true" className="-ml-0.5 size-5" />
                    Save Final Meeting
                    </button>
                    </div>
                    )}

                           
                            </div>
                            
                          )}

                          
                          </div>
                          
                        )}
                        <dd className="sm:col-span-2 text-gray-700">
                        <WitnessStatus
                        appraisal={currentAppraisal}
                        status={status}
                        onAutoAdvance={autoAdvanceStatus}
                        />
                        </dd>
                        </div>

                        <dl className="divide-y divide-gray-100">
                        {[
                        ["Author", currentAppraisal?.Author],
                        ["Employee Involved", currentAppraisal?.StaffName],
                        [
                        "Date of Incident",
                        currentAppraisal?.DateOfIncident
                        ? format(new Date(currentAppraisal.DateOfIncident), "MMM dd, yyyy")
                        : "N/A",
                        ],
                        ["Time of Incident", currentAppraisal?.TimeOfIncident],
                        ["Location of Incident", currentAppraisal?.LocationOfIncident],
                        ["Description of Incident", currentAppraisal?.DescriptionOfIncident],
                        ["Policy Violated", currentAppraisal?.PolicyViolated],
                        ["Witnesses", currentAppraisal?.Witnesses],
                        ].map(([label, value], idx) => (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4" key={label}>
                        <dt className="font-medium text-gray-900">{label}</dt>
                        <dd className="sm:col-span-2 text-gray-700">{value || "N/A"}</dd>
                        </div>
                        ))}

                        {Array.isArray(currentAppraisal?.statements) &&
                        currentAppraisal.statements.length > 0 && (
                        <div className="sm:col-span-3 py-4">
                        <h4 className="text-lg font-semibold mb-2 text-gray-900">Statements</h4>
                        <ul className="space-y-2">
                        {currentAppraisal.statements.map((statement, idx) => (
                          <li key={idx} className="bg-gray-50 rounded p-3">
                          <p className="text-sm text-gray-700 mb-1">
                          <span className="font-semibold">Staff:</span> {statement.StaffName || "N/A"}
                          </p>
                          <p className="text-sm text-gray-700">
                          <span className="font-semibold">Statement:</span> {statement.Statement || "N/A"}
                          </p>
                          </li>
                        ))}
                        </ul>
                        </div>
                        )}

                        {/* Editable fields */}
            {EDITABLE_FIELDS.map(({ label, key }) => (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4" key={key}>
                <dt className="font-medium text-gray-900">{/* empty for spacing */}</dt>
                <dd className="sm:col-span-2 text-gray-700">
                  <EditableField
                    label={label}
                    value={getFieldValue(key)}
                    fieldKey={key}
                    appraisalId={currentAppraisal?.id}
                    onUpdated={() => setRefresh((r) => r + 1)}
                    importAISummary={
                      key === "EmployeePerspective"
                        ? handleImportAISummary
                        : undefined
                    }
                  />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
        </div>
    </>
  );
}
